import { useState, useRef, useEffect } from 'react';
import DatePicker from './DatePicker';
import { parseDate } from '../utils/dateUtils';
import { useTranslation } from '../i18n';
import './HolidayForm.scss';

/**
 * Reusable form component for adding or editing holidays.
 * Used by HolidayModal for both inline editing and adding new holidays.
 * 
 * @param {Object} props
 * @param {Object} [props.holiday] - Existing holiday to edit (null for add mode)
 * @param {Function} props.onSave - Handler for save (formData) => void
 * @param {Function} props.onCancel - Handler for cancel
 * @param {Function} [props.onDelete] - Handler for delete (id) => void (edit mode only)
 * @param {boolean} [props.isInline=false] - True when editing inline in list
 * @param {string} [props.submitLabel] - Custom submit button label (default: "Add" or "Save")
 */
function HolidayForm({ holiday, onSave, onCancel, onDelete, isInline = false, submitLabel }) {
  const { t, formatDate } = useTranslation();
  const isEditing = !!holiday;
  const defaultLabel = isEditing ? t('common.save') : t('common.add');
  const finalSubmitLabel = submitLabel || defaultLabel;

  // Initialize form state
  const [formData, setFormData] = useState(() => {
    if (holiday) {
      return {
        title: holiday.title,
        startDate: parseDate(holiday.startDate),
        endDate: parseDate(holiday.endDate),
        description: holiday.description || ''
      };
    }
    
    // Default for new holiday
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
      title: '',
      startDate: today,
      endDate: today,
      description: ''
    };
  });

  const [isMultiDay, setIsMultiDay] = useState(() => {
    if (holiday) {
      return holiday.startDate !== holiday.endDate;
    }
    return false;
  });

  const [activePicker, setActivePicker] = useState(null); // 'start' | 'end' | null
  const inputRef = useRef(null);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const handleToggleMultiDay = (checked) => {
    setIsMultiDay(checked);
    if (!checked) {
      // When switching to single-day, set end date to start date
      setFormData({ ...formData, endDate: formData.startDate });
    }
    setActivePicker(null);
  };

  const handleStartDateChange = (newDate) => {
    setFormData({ 
      ...formData, 
      startDate: newDate,
      endDate: isMultiDay ? formData.endDate : newDate
    });
    setActivePicker(null);
  };

  const handleEndDateChange = (newDate) => {
    setFormData({ ...formData, endDate: newDate });
    setActivePicker(null);
  };

  const handleSave = () => {
    if (!formData.title.trim() || !formData.startDate || !formData.endDate) return;

    onSave({
      title: formData.title.trim(),
      startDate: formData.startDate,
      endDate: formData.endDate,
      description: formData.description.trim()
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  const formatDateDisplay = (date) => {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : parseDate(date);
    return formatDate(dateObj);
  };

  const formClass = [
    'holiday-form',
    isInline ? '' : 'holiday-form--standalone'
  ].filter(Boolean).join(' ');

  return (
    <div className={formClass} onKeyDown={handleKeyDown}>
      <input
        ref={inputRef}
        type="text"
        className="holiday-form__input"
        placeholder={t('holiday.placeholder')}
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        maxLength={100}
      />
      
      <div className="holiday-form__date-section">
        <div className="holiday-form__date-row">
          <div className="holiday-form__date-field">
            <label className="holiday-form__date-label">
              {isMultiDay ? t('holiday.startDate') : t('holiday.date')}
            </label>
            <button
              type="button"
              className="holiday-form__date-btn"
              onClick={() => setActivePicker(activePicker === 'start' ? null : 'start')}
            >
              {formatDateDisplay(formData.startDate)}
            </button>
            {activePicker === 'start' && (
              <DatePicker
                value={formData.startDate}
                onChange={handleStartDateChange}
                onClose={() => setActivePicker(null)}
                maxDate={isMultiDay ? formData.endDate : undefined}
              />
            )}
          </div>
          
          {isMultiDay && (
            <div className="holiday-form__date-field">
              <label className="holiday-form__date-label">{t('holiday.endDate')}</label>
              <button
                type="button"
                className="holiday-form__date-btn"
                onClick={() => setActivePicker(activePicker === 'end' ? null : 'end')}
              >
                {formatDateDisplay(formData.endDate)}
              </button>
              {activePicker === 'end' && (
                <DatePicker
                  value={formData.endDate}
                  onChange={handleEndDateChange}
                  onClose={() => setActivePicker(null)}
                  minDate={isMultiDay ? formData.startDate : undefined}
                />
              )}
            </div>
          )}
        </div>
        
        <label className="holiday-form__checkbox">
          <input
            type="checkbox"
            checked={isMultiDay}
            onChange={(e) => handleToggleMultiDay(e.target.checked)}
          />
          <span>{t('holiday.multiDay')}</span>
        </label>
      </div>
      
      <div className="holiday-form__actions">
        {isEditing && onDelete && (
          <button 
            type="button"
            className="holiday-form__btn holiday-form__btn--delete" 
            onClick={() => onDelete(holiday.id)}
          >
            {t('common.delete')}
          </button>
        )}
        <div className="holiday-form__actions-right">
          <button className="holiday-form__btn holiday-form__btn--cancel" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button 
            className="holiday-form__btn holiday-form__btn--save" 
            onClick={handleSave}
            disabled={!formData.title.trim() || !formData.startDate || !formData.endDate}
          >
            {finalSubmitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default HolidayForm;
